/**
 * DailyAssignments
 * Shows assignments due today or earlier as horizontal list cards.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { isDateOverdue, formatDateYMD, getTodayInChina } from '../../../utils/timestampUtils';
import { formatLessonLabel } from '../../../utils/lessonTitle';
import { isAutoGeneratedExtra } from '../../../utils/assignmentLabels';
import { getTopicDisplayName } from '../../../config/topics';
import { getCourseBehavior } from '../../../services/courseService';
import { optimizeImageUrl, IMAGE_SIZES } from '../../../utils/imageOptimization';
import { getProjectConfig } from '../../../data/projectsConfig';
import './DailyAssignments.css';

/**
 * Format assignment label for display
 */
function formatAssignmentLabel(assignment, lessonLabelCache) {
  if (assignment.project_id) {
    return assignment.projectName || 'Project';
  }

  return formatLessonLabel(
    assignment.topic,
    assignment.batch,
    assignment.lesson_number,
    lessonLabelCache
  );
}

/**
 * The lesson's video frame, from the book lessons the dashboard already loads; a lesson
 * without one (all of NCE) shows its course cover. Nothing until the book has loaded,
 * so a card never flashes the cover before its real frame.
 */
function lessonThumb(assignment, bookLessons) {
  const lessons = bookLessons[`${assignment.topic}_${assignment.batch}`];
  if (!lessons) return null;
  // A project spans several lessons: show its first one.
  const n = assignment.project_id
    ? parseInt(getProjectConfig(assignment.project_id)?.pages?.[0], 10)
    : Number(assignment.lesson_number);
  // dashboardThumbs names which video's frame to use: true = main, or e.g. 'sentences'.
  const flag = getCourseBehavior(assignment.topic, 'dashboardThumbs');
  const video = flag === true ? 'main' : flag;
  return lessons.find(l => parseInt(l.lessonNumber, 10) === n)?.videos?.[video]?.thumb
    || getCourseBehavior(assignment.topic, 'thumbnail', null);
}

/**
 * What the kid will actually do, as one quiet tag — or none for ordinary practice.
 * First match wins: a redo is a redo even if combined; memorize is the harder
 * variant of combined; 新课 only when nothing else applies.
 */
function assignmentTag(assignment) {
  const reviewIndex = assignment.reviewIndex ?? assignment.review_index ?? 0;
  if (reviewIndex < 0 && isAutoGeneratedExtra(assignment.extra_source)) return { label: '返工', kind: 'redo' };
  if (assignment.memorize) return { label: '背诵', kind: 'memo' };
  if (assignment.combined_mode) return { label: '整段读', kind: 'whole' };
  if (assignment.isFirstTime) return { label: '新课', kind: 'new' };
  return null;
}

/**
 * Single assignment card
 * Whole card is clickable, XP reward tile on right shows locked state
 */
function AssignmentCard({
  assignment,
  studentId,
  lessonLabelCache,
  itemCountsCache = {},
  isTeacherView,
  onTeacherClick,
  locked = false,
  onLockedClick,
  bookLessons = null,
}) {
  const label = formatAssignmentLabel(assignment, lessonLabelCache);
  // The box is reserved whenever the course has thumbnails, so the card doesn't
  // jump when the book lessons (and with them the image) arrive a moment later.
  // Callers that never load book lessons (null) get no box at all.
  const showThumb = !!bookLessons && !!getCourseBehavior(assignment.topic, 'dashboardThumbs');
  const thumb = showThumb ? lessonThumb(assignment, bookLessons) : null;


  // Get reviewIndex for QR link and E-assignment detection
  const reviewIndex = assignment.reviewIndex ?? assignment.review_index ?? 0;

  const getQRLink = () => {
    if (assignment.project_id) {
      // Always pin to the specific assignment row via aid — a class can have
      // multiple project rows (different combined_mode / wave_id) and the
      // fallback resolver in useAssignmentTracking only matches review_index=0,
      // wave_id=0 without it.
      if (reviewIndex < 0) {
        return `/project/${assignment.project_id}?aid=${assignment.id}&review=${reviewIndex}`;
      }
      return `/project/${assignment.project_id}?aid=${assignment.id}`;
    }
    if (assignment.assignment_type === 'quiz') {
      const tc = assignment.target_crown || 1;
      return `/lesson/${assignment.topic}/${assignment.batch}/${assignment.lesson_number}?targetCrown=${tc}`;
    }
    const waveParam = assignment.wave_id ?? 0;
    return `/qr/${assignment.topic}/${assignment.batch}/${assignment.lesson_number}?review=${reviewIndex}&wave=${waveParam}&aid=${assignment.id}`;
  };

  // Secondary line: topic and book. Lateness sits quietly on the right, and only
  // on overdue cards — this list is due-today-or-earlier, so any other card is "today".
  const topicName = assignment.topic ? getTopicDisplayName(assignment.topic) : '';
  const bookNum = assignment.batch ? assignment.batch.replace(/[^\d]/g, '') : '';
  const isOverdue = assignment.due_date && isDateOverdue(assignment.due_date);
  const tag = assignmentTag(assignment);
  const daysLate = isOverdue
    ? Math.round((Date.parse(getTodayInChina()) - Date.parse(formatDateYMD(assignment.due_date))) / 86400000)
    : 0;

  const cardContent = (
    <>
      {showThumb && (
        <span className="todo-card-thumb" aria-hidden="true">
          {thumb && (
            <img src={optimizeImageUrl(thumb, { width: IMAGE_SIZES.CARD })} alt="" width={84} height={48} loading="lazy" />
          )}
        </span>
      )}
      <div className="todo-card-info">
        <div className="todo-card-title">{label}</div>
        {topicName && (
          <div className="todo-card-secondary">
            <span>{topicName}{bookNum ? ` 书${bookNum}` : ''}</span>
            {tag && <span className={`todo-card-tag todo-card-tag--${tag.kind}`}>{tag.label}</span>}
          </div>
        )}
      </div>
      {daysLate > 0 && <span className="todo-card-late">逾期 {daysLate} 天</span>}
    </>
  );

  // Card classes - only auto-generated E's get pulsing red dot
  const isExtraAssignment = reviewIndex < 0 && isAutoGeneratedExtra(assignment.extra_source);
  const isQuizAssignment = assignment.assignment_type === 'quiz';
  let cardClass = 'todo-card';
  if (isExtraAssignment) cardClass += ' extra-assignment';
  if (isQuizAssignment) cardClass += ' quiz-assignment';
  if (isOverdue) cardClass += ' overdue';

  // Teacher view: button instead of link
  if (isTeacherView) {
    return (
      <button
        className={cardClass}
        onClick={() => onTeacherClick?.(assignment)}
      >
        {cardContent}
      </button>
    );
  }

  // Locked behind the daily test. A button, not a disabled Link — the tap has to
  // still land so we can explain WHY. A card that simply does nothing is the one
  // outcome guaranteed to read as the app being broken.
  if (locked) {
    return (
      <button
        className={`${cardClass} todo-card--locked`}
        onClick={() => onLockedClick?.(assignment)}
        aria-label={`${label}（需要先完成每日复习）`}
      >
        {cardContent}
        <span className="todo-card-lock" aria-hidden="true">🔒</span>
      </button>
    );
  }

  // Student view: whole card is a link
  return (
    <Link
      to={getQRLink()}
      state={{ fromDashboard: true, dashboardPath: `/dashboard/${studentId}` }}
      className={cardClass}
    >
      {cardContent}
    </Link>
  );
}

/**
 * Main daily assignments component
 */
export default function DailyAssignments({
  assignments,  // Direct array of assignments due today or earlier
  studentId,
  lessonLabelCache,
  itemCountsCache = {},
  isTeacherView = false,
  onTeacherClick,
  compact = false,  // Compact mode for teacher grid view
  locked = false,   // Teacher has walled homework behind the daily test
  onLockedClick,
  bookLessons = null, // `${topic}_${batch}` → lessons, for per-lesson thumbnails; null = none
}) {

  // Sort E assignments to top so kids do them first
  // Slice first — .sort() mutates in place and we must not mutate the prop array
  const visibleAssignments = (assignments || [])
    .slice()
    .sort((a, b) => {
      const aReview = a.reviewIndex ?? a.review_index ?? 0;
      const bReview = b.reviewIndex ?? b.review_index ?? 0;
      // Only auto-generated E's sort to top
      const aIsExtra = aReview < 0 && isAutoGeneratedExtra(a.extra_source);
      const bIsExtra = bReview < 0 && isAutoGeneratedExtra(b.extra_source);
      // E assignments first
      if (aIsExtra && !bIsExtra) return -1;
      if (!aIsExtra && bIsExtra) return 1;
      return 0; // preserve original order otherwise
    });

  if (visibleAssignments.length === 0) {
    return (
      <div className="daily-empty illustrated">
        <div className="empty-illustration">
          <div className="empty-mascot">✅</div>
        </div>
        <p className="empty-title">作业完成</p>
      </div>
    );
  }

  return (
    <div className={`todo-container${compact ? ' compact' : ''}`}>
      <div className="todo-list">
        {visibleAssignments.map((assignment) => (
          <AssignmentCard
            key={assignment.id}
            assignment={assignment}
            studentId={studentId}
            lessonLabelCache={lessonLabelCache}
            itemCountsCache={itemCountsCache}
            isTeacherView={isTeacherView}
            onTeacherClick={onTeacherClick}
            locked={locked}
            onLockedClick={onLockedClick}
            bookLessons={bookLessons}
          />
        ))}
      </div>

    </div>
  );
}
